import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type SelectSearchFormOptions<V = any> = {
  id: string;
  label: string;
  description?: string;
  value: V;
  customClass?: string;
}[];
export function SelectSearchForm<V = any>({
  form,
  fieldName,
  className = '',
  optionId = 'id',
  label,
  description,
  placeholder = '',
  options,
  searchPlaceholder,
  searchEmpty,
  inline = false,
  disabled = false,
  getValuePlaceholder = () => undefined,
  onSearchChange,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  form: any;
  fieldName: string;
  className?: string;
  optionId?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  options: SelectSearchFormOptions<V>;
  inline?: boolean;
  searchPlaceholder?: string;
  searchEmpty?: string;
  disabled?: boolean;
  getValuePlaceholder?: (selectedValue: V) => string | undefined;
  onSearchChange: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    controlledOnOpenChange?.(next);
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <div
            className={cn(
              className,
              inline
                ? 'flex flex-row items-center justify-between gap-2 space-y-0'
                : 'flex flex-col gap-2',
            )}
          >
            {label && <FormLabel>{label}</FormLabel>}

            <Popover
              open={open}
              onOpenChange={setOpen}
            >
              <PopoverTrigger
                asChild
                className="h-10"
                disabled={disabled}
              >
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      'justify-between',
                      inline ? 'w-[240px]' : 'w-full',
                    )}
                  >
                    <span
                      className={cn(
                        !field.value ? 'text-muted-foreground' : '',
                      )}
                    >
                      {getValuePlaceholder(field.value) ?? placeholder}
                    </span>
                    <ChevronDownIcon className="ml-2 h-4 w-4 flex-none shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>

              <PopoverContent
                className={cn('p-0', inline ? 'w-[240px]' : 'w-[500px]')}
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={searchPlaceholder}
                    className="h-9"
                    onValueChange={onSearchChange}
                  />
                  <CommandList>
                    <CommandEmpty>
                      <span className="text-muted-foreground">
                        {searchEmpty}
                      </span>
                    </CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        let isSelected: boolean;
                        if (
                          field.value &&
                          typeof field.value === 'object' &&
                          optionId in field.value &&
                          option.value &&
                          typeof option.value === 'object' &&
                          optionId in option.value
                        ) {
                          isSelected =
                            (option.value as any)[optionId] ===
                            field.value[optionId];
                        } else {
                          isSelected = option.value === field.value;
                        }

                        return (
                          <CommandItem
                            key={option.id}
                            value={option.id}
                            className={cn('cursor-pointer', option.customClass)}
                            onSelect={() => {
                              setOpen(false);

                              if (isSelected) {
                                form.setValue(fieldName, null, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              } else {
                                form.setValue(fieldName, option.value, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <CheckIcon
                                className={cn(
                                  'ml-auto h-4 w-4',
                                  isSelected ? 'opacity-100' : 'opacity-0',
                                )}
                              />

                              <div className="flex flex-col items-start justify-center">
                                <div className="flex items-center">
                                  {option.label}
                                </div>

                                {option.description && (
                                  <span className="text-muted-foreground text-sm">
                                    {option.description}
                                  </span>
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {description && <FormDescription>{description}</FormDescription>}

          <FormMessage />
        </FormItem>
      )}
    />
  );
}
